/**
 * A worked example for every capability the catalogue marks Available.
 *
 * WHY EVERY BUILT CARD HAS ONE. A card is a claim, and a claim with no example is what an
 * evaluator has to take on trust. The example is what makes the card checkable: here is the
 * thing you do, here is what comes back, and here is the part that surprises people.
 *
 * PLANNED CARDS DELIBERATELY HAVE NONE.
 *
 * WHAT IS REAL AND WHAT IS ILLUSTRATIVE. Shapes, field names, refusal codes and behaviour are
 * taken from the running services and are meant to be checkable; capabilityExamples.test.ts
 * checks that every code quoted here is one the backend raises. Record identifiers and values
 * are from the Peak Medical demonstration tenant where one was captured (the Titanium Screw line
 * and its sub-assembly, the approved supplier rows) and are otherwise a consistent illustration
 * around the same product. No number here is a measurement of a real customer's data.
 */

export type Step = {
    /** What the person does. */
    does: string;
    /** What happens, in terms they can check against their own screen. */
    then: string;
};

export type Sample = {
    caption: string;
    /** `json` and `text` are monospaced; `quote` is the product's own wording, rendered as prose. */
    kind: "json" | "text" | "quote";
    body: string;
};

export type CapabilityExample = {
    slug: string;
    /** The situation, in one sentence. */
    scenario: string;
    steps: Step[];
    samples?: Sample[];
    /** The thing most people get wrong about this capability. Every example has one. */
    trap: {heading: string; body: string};
    /** The same thing from a pipeline, where there is an endpoint for it. */
    api?: string;
    /** A guide that goes further, if one exists. */
    guide?: {file: string; label: string};
};

export const EXAMPLES: CapabilityExample[] = [

    // ── Controlled records ──────────────────────────────────────────────────────────────────
    {
        slug: "records-and-revisions",
        scenario: "The gowning SOP needs a new step. SOP-00009 revision 2 is released and in force, and people are trained on it.",
        steps: [
            {does: "Open SOP-00009 and choose Revise.",
             then: "Revision 3 is created as a DRAFT with the form and answers carried forward. Revision 2 stays RELEASED and is what everybody reads until 3 is released."},
            {does: "Make the change on the draft and submit it for approval.",
             then: "The matrix resolves who has to sign. Nothing about revision 2 changes while the draft is in review."},
            {does: "The last signature is given and revision 3 is released.",
             then: "Revision 2 becomes SUPERSEDED and stays readable as what was in force; revision 3 is current. Anybody whose training item requires retraining is outstanding again."},
        ],
        samples: [
            {caption: "What the product says before an action it cannot undo", kind: "quote",
             body: "Releasing, deprecating and voiding cannot be undone. A released revision is frozen; to change it, revise it."},
        ],
        trap: {
            heading: "A released revision is frozen, and that is not a bug",
            body: "The most common thing a newcomer reports is that they cannot edit a document. They are looking at a released revision, which nobody can edit, and the edit button is not hidden to be unkind: it is absent because the thing it would do is forbidden. Revise, and the draft is editable.",
        },
        api: "PUT /t3/v1/object_revisions with LifecycleAction: Revise",
        guide: {file: "lifecycle.html", label: "Record lifecycle"},
    },
    {
        slug: "forms-designed-by-owners",
        scenario: "The quality team wants a 'Batch reference' field on the Issue form, required, and a free-text 'Containment' field that is not.",
        steps: [
            {does: "Open the form designer for the Issue type and add the two fields from the vocabulary: a short text, required, and a long text.",
             then: "The definition is saved as the type's shape. Every new Issue asks both; existing records keep the answers they have."},
            {does: "On one particular Issue, as its owner, choose Design this record's form and add a 'Supplier notified' yes/no field for that record alone.",
             then: "The record's own form carries the extra field; the type's shape is untouched. Saving re-reads the declared fields from the shape, so a field removed from the design is not silently kept."},
            {does: "Try to save a form with a field whose type the vocabulary does not know.",
             then: "The whole document is refused with every problem listed, not the first one; nothing half-saved locks the record shut."},
        ],
        trap: {
            heading: "A form reaches the dialog by a link and the record by the shape",
            body: "Two carriers, and they are different: the link is what makes the create dialog ask the questions, and the shape is what the record validates against. A shape seeded without its link asked nothing at creation and nobody noticed until a record arrived empty. The designer writes both.",
        },
        guide: {file: "form-designer.html", label: "Form designer"},
    },
    {
        slug: "audit-history",
        scenario: "An auditor asks who changed the acceptance criteria on a procedure and when, and whether it was before or after the release.",
        steps: [
            {does: "Open the record's History tab.",
             then: "Every change, lifecycle action, revision and signature, newest first, with the person and the time. A field change shows the field, the old value and the new."},
            {does: "Ask the assistant: what changed on SOP-00009 since it was released?",
             then: "The same history, read under your persona, summarised with the entries it drew on."},
        ],
        samples: [
            {caption: "One entry, as the API returns it", kind: "json",
             body: "{ \"Action\": \"Field changed\",\n  \"Field\": \"acceptanceCriteria\",\n  \"By\": \"Amara Okonkwo\",\n  \"At\": \"2026-10-30T14:12:08\",\n  \"Revision\": \"2\" }"},
        ],
        trap: {
            heading: "The history is the service's, not the screen's",
            body: "A change made through the public API, by a script, or by the assistant's approved proposal leaves exactly the same entry as one made by hand, because the entry is written where the change is made. A history that only recorded what the screen did would be silent about the writes that matter most.",
        },
        api: "GET /t3/v1/object_audit_history?USID=…",
    },
    {
        slug: "attachments-you-can-ask",
        scenario: "Somewhere in the certificates attached to forty supplier records is the one that states a shelf life for a resin, and nobody remembers which supplier.",
        steps: [
            {does: "On the Suppliers list, search inside the attached files for 'shelf life resin'.",
             then: "The passages most like the question, by meaning and by words together, each naming the supplier record, the file and the page."},
            {does: "Tick the two likely suppliers and ask: what shelf life does each certificate state?",
             then: "An answer drawn from those files only, with a citation per claim, and 'the files do not say' where they do not."},
        ],
        trap: {
            heading: "The corpus is scoped before the question, never filtered after",
            body: "The search covers the records your persona can open and no others, decided before the question is run. Filtering results afterwards would still have read the passage, and a model that read a passage can leak it in a summary. A persona that cannot see a supplier cannot ask its certificate anything.",
        },
        api: "POST /t3/v1/attachment_questions",
        guide: {file: "asking-about-attachments.html", label: "Asking about attachments"},
    },

    // ── Who sees what ───────────────────────────────────────────────────────────────────────
    {
        slug: "personas-and-visibility",
        scenario: "A quality engineer holds two positions and a work group. A procedure owned by Manufacturing is shared with the work group, and she cannot find it.",
        steps: [
            {does: "Open the persona switch and see which persona she is acting as.",
             then: "Her default position. The procedure is shared with the work group, not with the position, so under this persona it does not exist."},
            {does: "Switch to the work group.",
             then: "The procedure appears in the list, and its Visibility tab says: owned by Manufacturing, shared with this work group at revision 2, this persona may read."},
        ],
        samples: [
            {caption: "What the assistant says under the wrong persona", kind: "quote",
             body: "No record you can see matches SOP-00031. If you expected one, check the persona you are acting as."},
        ],
        trap: {
            heading: "A share names the revision",
            body: "Sharing revision 2 with a group shares revision 2. When revision 3 is released the group does not automatically see it, because what they were given to read is what they were given. The Visibility tab says which revision a share is for, and re-sharing is one click, but it is a click somebody makes on purpose.",
        },
        guide: {file: "visibility.html", label: "Visibility"},
    },
    {
        slug: "sight-per-node",
        scenario: "The finished-good assembly FG-00017 contains a sub-assembly whose drawings are restricted to a design group you are not in.",
        steps: [
            {does: "Open FG-00017's Bill of materials tab as a manufacturing persona.",
             then: "The visible lines are drawn in full. The restricted sub-assembly is one row: its ID in grey, 'Not visible to you', no link, and nothing below it, because what you cannot see is not walked."},
            {does: "Read the summary above the lines.",
             then: "'3 lines over 2 levels. No standard cost total: 1 line uncosted or hidden. 1 not visible to you and not walked.' The total is withheld rather than printed without the hidden line."},
        ],
        trap: {
            heading: "A total over what you cannot see would tell you what you cannot see",
            body: "An inventory's quantity on hand summed over every lot would hand a reader the stock of lots they may not open, and it would differ per reader, so it is never written on the record. The sum above the list is over what you can see, and the count of what you cannot is said beside it.",
        },
        api: "GET /t3/v1/bom_explosion?ItemObjectUSID=…",
    },
    {
        slug: "external-reviewer",
        scenario: "A consultant outside the company has to review one validation protocol before it is released, and must see nothing else.",
        steps: [
            {does: "Share the protocol's draft revision with the consultant as an external document reviewer.",
             then: "They receive a link to that revision. Opening it shows the document and a place to comment, and no list, search or other record."},
            {does: "They comment; you revise; the next revision is a new share.",
             then: "What they reviewed is the revision they were given, and the record of their comments is on it."},
        ],
        trap: {
            heading: "One document means one revision",
            body: "The share is to the revision, not to the record, so a reviewer given revision 2 does not see revision 3 appear under them. That is the point: their review is of a fixed thing, and the fixed thing is what the signature later refers to.",
        },
    },
    {
        slug: "controlled-folders",
        scenario: "The clinical team keeps its study documents together, and the contracts for each site must be seen by the contracts team only.",
        steps: [
            {does: "Under Folders, create Clinical, open Access and give the Clinical Operations position View and the study coordinator File.",
             then: "Clinical Operations can see every record filed in Clinical, read-only; the coordinator can also file records there."},
            {does: "Inside Clinical, create Contracts, turn off Take the grants of the folders above, and give the Contracts user group View.",
             then: "Clinical Operations no longer sees Contracts at all -- not its records, and not its name. The Contracts group does."},
            {does: "Open a monitoring plan you own and, under Folder, file it in Clinical.",
             then: "It appears in Clinical for everyone the folder is given to, and the folder's history says who filed it and when. Filing it somewhere else moves it: a record lives in one folder."},
        ],
        trap: {
            heading: "A folder gives sight, never editing",
            body: "Giving a folder to a team lets them read what is in it. It does not let them change a record, even if their role could change records they own: what a share made by a folder allows is Read only. Editing still comes from owning the record or from a share made on the record itself.",
        },
    },

    // ── Approval and electronic signature ───────────────────────────────────────────────────
    {
        slug: "approval-matrix",
        scenario: "Procedures need a quality review and then a department head's approval, in that order, and a department head who wrote the procedure must not approve their own.",
        steps: [
            {does: "On the approval matrix, add a row for the Procedure type with two slots in two stages: Reviewed, by the Quality Director position; Approved, by the Operations Director position.",
             then: "Author may sign is off, so the author is excluded from either slot even when they hold the position."},
            {does: "Submit a procedure.",
             then: "Each slot is resolved to the people who hold the persona now. If a slot resolves to nobody, the submission is refused now with the slot named, rather than leaving an approval that can never finish."},
        ],
        trap: {
            heading: "A slot names a persona, not a person",
            body: "The most common support question is 'why isn't my name offered for the slot'. The slot went to a position, and the asker is acting as a different persona, or does not hold that position, or wrote the record. The slot does not know names; it knows who holds the persona today.",
        },
        guide: {file: "approvals.html", label: "Approvals and electronic signatures"},
    },
    {
        slug: "electronic-signature",
        scenario: "You are the second of two approvers on a released-pending procedure, and you are signing from a shared bench terminal.",
        steps: [
            {does: "Open Approvals, read the request as it stood when it was submitted, and choose Approve.",
             then: "The dialog asks for your password, not the session's. A wrong password counts on the same lockout counter as the login screen."},
            {does: "Enter it.",
             then: "The signature appears with your printed name, the time, the meaning of the slot and the note that you re-authenticated. When the last slot is signed the record is released."},
        ],
        samples: [
            {caption: "A rejection has to say why", kind: "quote",
             body: "A rejection without a comment is refused: 'rejected' with no reason sends the author back to guess."},
        ],
        trap: {
            heading: "An unattended screen is not a signing authority",
            body: "21 CFR 11.200(a)(1) asks for two identification components at each signing. A session proves who logged in an hour ago, not who is at the keyboard now, which is why the password is asked every time and why a PIN would not do.",
        },
        guide: {file: "approvals.html", label: "Approvals and electronic signatures"},
    },
    {
        slug: "stale-submission",
        scenario: "A procedure was submitted, the first approver signed, and the author then fixed a typo on the draft.",
        steps: [
            {does: "The second approver opens the request and tries to sign.",
             then: "Refused: the revision changed after it was submitted, so the snapshot the first signature was given against no longer matches what is on screen."},
            {does: "The author withdraws the approval, which the product offers as the way out, and resubmits.",
             then: "A fresh snapshot, both slots open again, and the first approver signs again for what is now there."},
        ],
        samples: [
            {caption: "The refusal, by code", kind: "text",
             body: "CC-OAPR-STAL  The revision changed after it was submitted for approval; withdraw and resubmit."},
        ],
        trap: {
            heading: "The first signature was not wrong, and it is still invalid",
            body: "Nothing about the first approver's judgement changed. What changed is that nobody can reconstruct exactly what they signed for once the draft moved, so a signature given before the change cannot be carried over a change, however small. A typo is a resubmission.",
        },
    },
    {
        slug: "approval-by-amount",
        scenario: "Orders up to 5,000 USD need the buyer's manager; up to 50,000 the operations director as well; above that, nobody has been named yet.",
        steps: [
            {does: "On the Purchase Order type's row in the matrix, add two amount bands with their slots, and no unbanded row.",
             then: "A 3,200 USD order resolves the first band. A 20,000 USD order resolves the second."},
            {does: "Submit a 70,000 USD order.",
             then: "Refused: the total is above every band and the type has no unbanded row, so there is no default to fall to. Somebody has to decide who approves an order that size before one can be submitted."},
        ],
        samples: [
            {caption: "The refusal, by code", kind: "text",
             body: "CC-OOAM-OVER  This order's total is above every amount band for its type and there is no unbanded row."},
        ],
        trap: {
            heading: "The order's stamped total decides, not the typed one",
            body: "The band is resolved against the total the server computed from the lines, the tax and the shipping at the currency's minor unit. A total typed by hand is not on the form at all, so an order cannot be submitted into a smaller band by rounding.",
        },
        guide: {file: "purchase-orders-and-receipts.html", label: "Purchase orders and receipts"},
    },

    // ── Change control ──────────────────────────────────────────────────────────────────────
    {
        slug: "change-orders",
        scenario: "Retiring one adhesive means revising two work instructions, a specification and the bill of materials of the three assemblies that use it.",
        steps: [
            {does: "Raise a change order with the justification and the impact assessment, and add the revisions it covers.",
             then: "The change order is DRAFT with its members listed; each member is the revision, so the review is of exactly those drafts."},
            {does: "Make the changes inside it, then submit the change order for approval.",
             then: "The matrix for change orders resolves the slots. While it is open, the bills of materials of the three assemblies can be changed under its control."},
            {does: "The change order is approved and closed.",
             then: "What is inside is released together, and the order's history records the lot."},
        ],
        trap: {
            heading: "A change order names revisions, not records",
            body: "Adding 'the gowning SOP' adds the revision you chose. A revision made later is not in the order unless it is added, which is what keeps the approval honest: the approvers signed for a list of specific things.",
        },
        guide: {file: "change-orders.html", label: "Change orders"},
    },
    {
        slug: "controlled-copy",
        scenario: "SOP-00011, the incident response plan, is approved and released with one PDF attached.",
        steps: [
            {does: "Open the record after release and read its files.",
             then: "Under Incident Response Policy.pdf sits Incident Response Policy (controlled copy).pdf, labelled controlled copy: the same pages, then an approval sheet naming SOP-00011 Rev 1, the release date, the file's SHA-256, the approval request and each signature with persona, meaning, printed name and time."},
            {does: "Download the original rather than the controlled copy.",
             then: "Every page carries UNCONTROLLED COPY; the file in storage is untouched. A superseded revision's copy says OBSOLETE, a draft's says DRAFT."},
            {does: "Try to remove the controlled copy, or attach a file to the released revision.",
             then: "Refused: CC-ATCH-GEN for the copy, CC-ATCH-FROZEN for the released revision. Revise the record to change its files; the new draft inherits the originals and not the sheet."},
        ],
        samples: [
            {caption: "The refusal on a released revision", kind: "text",
             body: "CC-ATCH-FROZEN  This revision is RELEASED: its files are part of what was released and cannot be changed. Revise the record to change them."},
        ],
        trap: {
            heading: "Generated, never uploaded",
            body: "A signed copy a person uploads can be wrong, missing or stale, and nothing checks it; a watermark a tenant has to switch on stays off. The controlled copy is made by the release itself from the record's own approvals, on by default per object type, and the nightly pass finishes anything a release could not. The files are also part of the signature's content hash, so a file swapped under a signature invalidates it.",
        },
        guide: {file: "lifecycle.html", label: "Record lifecycle"},
    },
    {
        slug: "bom-change-control",
        scenario: "FG-00017 is released and shipping. A line on its bill of materials has the wrong quantity.",
        steps: [
            {does: "Edit the line on the Bill of materials tab and save.",
             then: "Under the default policy the line is saved and a notice says: saved, uncontrolled, because this bill belongs to a RELEASED revision and no open change order names it. Under a policy set to refuse, the save is refused with the same reason and the code."},
            {does: "Raise a change order against FG-00017, add its current revision, and make the change again.",
             then: "Saved with no finding: an open change order names the revision, so the change is controlled, and the item's History tab shows a row naming the line and the change order."},
            {does: "Open As captured on the Bill of materials tab and choose the capture taken when revision 2 was released.",
             then: "The bill as it stood at release, read-only, with its fingerprint checked: what revision 2 contained, whatever has changed since."},
        ],
        samples: [
            {caption: "The refusal when the policy refuses", kind: "text",
             body: "CC-OBRL-UNCTL  This bill of materials cannot be changed: its parent revision is RELEASED and no open change order names it."},
        ],
        trap: {
            heading: "The bill is captured, not frozen into the revision",
            body: "Revising the part does not copy its bill forward, and changing the bill does not revise the part; the bill belongs to the item. So the control is the change order, not a revision binding, and the historical question is answered by the capture taken at release, and by the build plan's own snapshot for what was built.",
        },
        guide: {file: "bill-of-materials.html", label: "Bill of materials"},
    },

    {
        slug: "bom-compare",
        scenario: "FG-00017 was released at revision 2 in March. Someone asks what has changed in its bill since.",
        steps: [
            {does: "Open the Bill of materials tab and press Compare.",
             then: "From is the latest capture, the one taken when revision 2 was released; To is Now. One line reads: 1 added, 1 removed, 1 changed, 11 unchanged."},
            {does: "Read the rows.",
             then: "Added: a washer at 2 EA. Removed: the glue under the housing subassembly, at its own level. Changed: the screw, Quantity 4 → 5. Nothing else is listed, because nothing else differs."},
            {does: "Set To to the capture taken by hand yesterday, with its reason.",
             then: "The same comparison between two captures. Set both sides to the same capture and the answer is that the fingerprints match."},
        ],
        samples: [
            {caption: "A changed line on /t3/v1/bom_compare", kind: "json",
             body: "{\"Kind\": \"Changed\", \"Level\": 1, \"ObjectID\": \"CMP-00011\", \"Name\": \"Screw\", \"ReferenceDesignator\": \"B1-B4\",\n \"ListOfChange\": {\"Change\": [{\"Field\": \"Quantity\", \"Before\": \"4\", \"After\": \"5\"}]}}"},
        ],
        trap: {
            heading: "A line is identified by where it sits",
            body: "Not by its internal key. A capture taken before the keys were recorded, or a line removed and added back, would otherwise read as removed and added. The path of items down to the component, its designator and whether it is a substitute is what a reader means by 'the same line', so that is the key. Both sides cannot be Now: the bill as it stands compared with itself is refused rather than answered with nothing.",
        },
        guide: {file: "bill-of-materials.html", label: "Bill of materials"},
    },
    {
        slug: "bom-import-export",
        scenario: "The ERP holds the bill for a new assembly as forty rows of part number, quantity and unit. The assembly has just been created here.",
        steps: [
            {does: "Open the Bill of materials tab, press Import…, and choose the CSV, or paste the rows.",
             then: "The first row names the columns; Part, Quantity and Unit are read, and Designator, Cost, Currency, Configuration and Pinned when present. 40 rows to import."},
            {does: "Press Import.",
             then: "38 added, 2 refused, each refused row with its reason: one part number no item here carries, and one quantity of zero. The thirty-eight are on the bill; nothing was rolled back."},
            {does: "Fix the two rows in the sheet and import those two alone.",
             then: "2 added. The sheet is exhausted and the bill has forty lines."},
            {does: "Press Export CSV on another assembly's bill.",
             then: "The explosion, every level, as a CSV whose columns are the import's. Imported into a third item, its level 1 rows become that item's lines."},
        ],
        samples: [
            {caption: "Two rows answered on /t3/v1/bom_import", kind: "json",
             body: "{\"Row\": 7, \"ComponentObjectID\": \"CMP-00057\", \"Status\": \"Added\", \"USID\": \"4f1c…\"}\n{\"Row\": 12, \"ComponentObjectID\": \"CMP-99999\", \"Status\": \"Refused\", \"Code\": \"CC-BOMI-404\", \"Message\": \"No Item in this tenant has the ID CMP-99999.\"}"},
        ],
        trap: {
            heading: "One row at a time, and nothing is rolled back",
            body: "An import that undid forty good lines for one bad part number would send somebody back to the spreadsheet with no help. Each row goes through the same door as a typed line, so every rule applies, and each is answered on its own, so the refused rows are the list to fix. The file is parsed in the browser and the rows are sent; nothing is uploaded through a function, and nothing is downloaded through one.",
        },
        guide: {file: "bill-of-materials.html", label: "Bill of materials"},
    },

    // ── Training ────────────────────────────────────────────────────────────────────────────
    {
        slug: "training-follows-the-revision",
        scenario: "Revision 3 of the gowning SOP is released. Forty people were trained on revision 2.",
        steps: [
            {does: "Open My Training as one of the forty.",
             then: "The SOP is outstanding again with the date it became so, because the training item requires retraining on a new revision and the completion on file names revision 2."},
            {does: "Open Training Setup as the administrator and ask who has completed SOP-00009.",
             then: "Per person: which revision they completed, when, and whether that is current. Forty rows on revision 2, none yet on 3."},
        ],
        trap: {
            heading: "A completion is against a revision",
            body: "Training on 'the SOP' does not exist; training on revision 2 does. That is what makes 'everybody trained on the current revision' a question with an answer, and it is why a new revision with Require Retraining on makes forty people outstanding at once, on purpose.",
        },
        guide: {file: "training.html", label: "Training"},
    },
    {
        slug: "quizzes-and-signed-completion",
        scenario: "The gowning SOP's training should check that people read the new step, not just that they clicked through.",
        steps: [
            {does: "Open the quiz builder on revision 3 and write four questions, one on the new step.",
             then: "The quiz belongs to revision 3 and is frozen when revision 3 is released; revision 4 starts with a copy to edit."},
            {does: "A trainee reads the revision, takes the quiz and signs with their password.",
             then: "A completion record naming revision 3, the score, and the signature. A failed quiz records the attempt and leaves the item outstanding."},
        ],
        trap: {
            heading: "The quiz is part of the revision",
            body: "A question changed after release would change what forty signed completions had tested, so it cannot be. A quiz is edited on a draft and frozen with it, like everything else on a revision.",
        },
        guide: {file: "training.html", label: "Training"},
    },

    // ── Projects and plans ──────────────────────────────────────────────────────────────────
    {
        slug: "project-plans",
        scenario: "A new catheter's design control has four phases, and verification must not start on an unapproved design.",
        steps: [
            {does: "Build the plan: four phases, each with its milestones, each phase's approval through the matrix.",
             then: "The plan's status and each phase's are read from the approvals actually given. A phase with no approval is not approved, whatever a checkbox might have said."},
            {does: "Try to rely on phase 3 before phase 2 is approved.",
             then: "Phase 3's milestones can be planned, but the phase cannot be approved until phase 2 is, and the plan says which approval it is waiting on."},
        ],
        trap: {
            heading: "Assignment is not visibility",
            body: "Assigning somebody a milestone does not make the plan visible to them. Visibility is the plan's owner's and its shares'; a person assigned a task on a plan they cannot see is told so, and the fix is a share, not a different assignment.",
        },
        guide: {file: "project-plans.html", label: "Project plans"},
    },
    {
        slug: "projects-as-folders",
        scenario: "A customer's enquiry turned into a quote, a contract and a build plan, and six months later somebody asks for everything about it.",
        steps: [
            {does: "Open the project the quote request was filed in.",
             then: "Its members by kind: the request, the quote response, the contract, the build plan and the product, plus the files that belong to the project itself; its status, OPEN or CLOSED."},
            {does: "Ask the assistant what is in project PRJ-00001.",
             then: "The same list, under your persona, with each member's status."},
        ],
        trap: {
            heading: "A project files the logical record",
            body: "The quote response was revised twice since it was filed. It is still in the project, at its current revision, because the member is the record's identity rather than a revision. A folder that pointed at revisions would quietly lose its contents as they moved.",
        },
        api: "GET /t3/v1/projects",
    },

    // ── Quality records ─────────────────────────────────────────────────────────────────────
    {
        slug: "issues-and-capas",
        scenario: "A complaint about a cracked housing becomes an issue, and the investigation concludes a CAPA is needed.",
        steps: [
            {does: "Create an Issue of type Complaint and fill its form: the lot, the description, the severity.",
             then: "A DRAFT quality record. Submit moves it to review; approve for action commits the investigation."},
            {does: "Raise the CAPA from the issue.",
             then: "The CAPA names the issue that caused it and the issue lists the CAPA it caused. Closing either asks for its effectiveness check."},
        ],
        trap: {
            heading: "An issue and a CAPA are the same kind of record",
            body: "Both are quality records with a kind, a type and a workflow, which is what lets them link to each other and be listed, searched and asked about together. A product that built two separate things from the same wrong assumption would have two of every rule.",
        },
        api: "GET /t3/v1/quality_records",
    },
    {
        slug: "qms-dashboard",
        scenario: "Monday morning. What is waiting on you, and what is overdue?",
        steps: [
            {does: "Open the QMS home.",
             then: "Four sections read in one request under your persona: approvals waiting on you, training outstanding and overdue, quality records by workflow state, records by lifecycle state."},
            {does: "Click a count.",
             then: "The list it counted, filtered the same way, so the number and the list agree."},
        ],
        trap: {
            heading: "A count is of what you could open",
            body: "The dashboard counts states under your persona, so it never shows a number that leads to a refusal when you click it. A dashboard that counted the tenant's records and then refused half of them on the way in would be a list of things you cannot do.",
        },
    },

    // ── From a demand to a build ────────────────────────────────────────────────────────────
    {
        slug: "quote-requests",
        scenario: "A customer emails: forty titanium screws by the end of the month.",
        steps: [
            {does: "Record the demand as a quote request, or let the mail gateway post it.",
             then: "QR-00001 with the subject, the body, quantity 40 EA, the customer, the item it names, and when it actually arrived, which for a forwarded email is not now."},
            {does: "Ask the assistant to record a demand from the sentence.",
             then: "A proposal card with the fields it read out; approving it records the request under your persona."},
        ],
        samples: [
            {caption: "Posting one from a gateway", kind: "json",
             body: "POST /t3/v1/quote_requests\n{ \"Subject\": \"40 titanium screws\",\n  \"Quantity\": 40, \"UnitOfMeasure\": \"EA\",\n  \"ReceivedDatetime\": \"2026-10-29T08:14:00\" }"},
        ],
        trap: {
            heading: "The detection columns are writable and nothing here detects",
            body: "An integrator that classified an email can record what it concluded and how sure it was; the product does not run a classifier of its own. Confidence is a fraction between 0 and 1, and a percentage there would read as certainty to anything comparing it against a threshold.",
        },
        api: "POST /t3/v1/quote_requests",
    },
    {
        slug: "quote-responses",
        scenario: "The forty screws need a price, and the margin has to hold.",
        steps: [
            {does: "Draft a prepared quote against QR-00001 and add a line for the screw.",
             then: "The line shows what is on hand, the best supplier and its lead time, and the cost breakdown: material from the supplier's price, labour and equipment from the routing. The per-unit price and the margin are read, not typed."},
            {does: "Read the totals.",
             then: "Rounded once, at the end, to the currency's minor unit, so a column of lines never disagrees with its own total by a cent."},
        ],
        trap: {
            heading: "A quote with no currency has no total",
            body: "The terms carry the currency; the lines read it. Asking a quote for its lines before its terms are set is refused rather than answered in some default currency, because a total in the wrong currency looks exactly like a total.",
        },
    },
    {
        slug: "build-plans",
        scenario: "The quote is accepted. When will forty screws be finished?",
        steps: [
            {does: "Create a build plan from the quote: quantity 40, the item, a start date.",
             then: "The routing steps are read from the item's routing with their hours; the lead time is computed against the shift configuration, and the planned end date with it."},
            {does: "Assign people to the steps and capture a snapshot.",
             then: "The snapshot records the bill of materials and the routing the plan was built to, so 'what did this build contain' is answered later from the snapshot and not from today's bill."},
        ],
        trap: {
            heading: "Lead time is computed, never entered",
            body: "A lead time typed on a plan is a promise; one computed from steps, hours and shifts is a consequence, and it changes when the steps do. If the number is wrong, the thing to fix is a step or a shift, and the plan says which.",
        },
        guide: {file: "project-plans.html", label: "Project plans"},
    },
    {
        slug: "work-streams",
        scenario: "Sales wants to know how many demands are answered, how many planned, and which are stuck.",
        steps: [
            {does: "Open Work Streams.",
             then: "One stream per quote request you can see, with its step: request only, response drafted, or build plan created; the project it is filed in; and the responses and plans under it."},
            {does: "Ask the assistant what demand is in flight.",
             then: "The same streams, counted by step, under your persona."},
        ],
        trap: {
            heading: "A stream is derived, not maintained",
            body: "Nobody creates a work stream or moves it along. It is read from the records that exist: a request, the responses that name it, the plans that name those. A board somebody has to update is a board that is wrong by Friday.",
        },
    },

    // ── Bill of materials ───────────────────────────────────────────────────────────────────
    {
        slug: "bom-lines",
        scenario: "The housing sub-assembly SUB-00037 needs four screws at positions B1 to B4 and 0.5 kg of adhesive that is stocked by the kilogram and used by the gram.",
        steps: [
            {does: "On SUB-00037's Bill of materials tab, choose Add line, pick CMP-00057 Titanium screw, quantity 4, unit EA, designator B1-B4, standard cost 0.10 USD.",
             then: "Under the designator the dialog says 4 positions. The line is saved and the explosion re-reads: 4 EA per parent, 0.40 USD extended, 4 positions. B1-B3 with a quantity of 4 would have been refused (CC-ORBD-QTY), and a second line at B3 is refused as taken (CC-ORBD-TAKEN)."},
            {does: "Add the adhesive at 500 G.",
             then: "The line is in grams because that is what one sub-assembly consumes; the unit is on the line, not on the part, so the same adhesive can be stocked in kilograms."},
            {does: "Pin the screw line to the revision that was qualified.",
             then: "The line keeps meaning that revision after the screw is revised; the explosion marks it ⚲ and shows the pinned revision beside the current one."},
        ],
        trap: {
            heading: "Zero is refused, not stored",
            body: "A line of zero is either a deleted line somebody did not delete or a mistake, and a bill that reads 'contains 0 brackets' has to be read twice to mean nothing. The quantity has to be above zero; remove the line instead.",
        },
        guide: {file: "bill-of-materials.html", label: "Bill of materials"},
    },
    {
        slug: "bom-explosion-and-roll-up",
        scenario: "FG-00017 contains two SUB-00037, each with three screws, and four screws of its own. How many screws does one FG-00017 need, and what does one cost?",
        steps: [
            {does: "Open the Explosion view.",
             then: "Indented by level: SUB-00037 at 2 EA, under it CMP-00057 at 3 EA per parent and 6 EA extended, then CMP-00057 again at level 1, 4 EA. The summary says 3 lines over 2 levels and whether a cost total could be given."},
            {does: "Switch to Rolled up.",
             then: "One row per component: CMP-00057, 10 EA over 2 lines, Count. The adhesive, 1000 G over its paths. A component in a SET would read 'does not convert' rather than being dropped from the sum."},
        ],
        samples: [
            {caption: "The roll-up as the API returns it", kind: "json",
             body: "{ \"RolledUpComponent\": [\n    { \"ObjectID\": \"CMP-00057\", \"Name\": \"Titanium screw\",\n      \"Dimension\": \"Count\", \"UnitOfMeasure\": \"EA\",\n      \"Quantity\": \"10\", \"Lines\": 2, \"Convertible\": true } ],\n  \"RollUpComplete\": true }"},
        ],
        trap: {
            heading: "A total is given only when it would be the whole",
            body: "The standard cost total appears only when every line is costed, in one currency, and nothing was hidden from you; otherwise the summary says how many lines are uncosted or hidden. A total that silently left a line out would read exactly like one that did not.",
        },
        api: "GET /t3/v1/bom_explosion?ItemObjectUSID=… with idoc-response v1.BillOfMaterialsExplosion-002",
        guide: {file: "bill-of-materials.html", label: "Bill of materials"},
    },
    {
        slug: "where-used",
        scenario: "The titanium screw's supplier is changing the thread. Which products are affected?",
        steps: [
            {does: "Open CMP-00057's Bill of materials tab and choose Where used.",
             then: "Every bill that names it: SUB-00037 at 3 per one, FG-00017 at 4 per one directly and through the sub-assembly, each parent's parents in turn, with the configuration a line belongs to."},
            {does: "Ask the assistant which assemblies use CMP-00057.",
             then: "The same climb under your persona; a parent you may not open is its ID and is not climbed."},
        ],
        trap: {
            heading: "Where-used is the direction a change travels",
            body: "The explosion tells you what a product needs; where-used tells you what a part's change touches. A compatibility question about a component is always the second one, and it needs every level, not the immediate parents.",
        },
        api: "GET /t3/v1/bom_where_used?ItemObjectUSID=…",
    },
    {
        slug: "bom-configurations",
        scenario: "The screw kit ships plain or sterile, and the sterile one adds a wrap.",
        steps: [
            {does: "On FG-00017, choose New configuration: Sterile, extension -S.",
             then: "The select offers it. Lines filed under it belong to the sterile variant; base lines belong to both."},
            {does: "Add the wrap under Sterile, then explode the bill with Sterile chosen and with the default.",
             then: "The wrap is in the first explosion and not the second. Make Sterile the default and an explosion that names nothing takes it."},
            {does: "Retire a configuration nobody builds any more.",
             then: "It is no longer offered; the lines filed under it are kept, so a plan that was built to it still reads."},
        ],
        trap: {
            heading: "A configuration belongs to one item",
            body: "A line cannot be filed under another item's configuration, because the variant is of one bill. Trying to is refused with the configuration named, which is the mistake a copied line most often carries.",
        },
        api: "GET /t3/v1/bom_configurations?ItemObjectUSID=…",
    },
    {
        slug: "cycle-prevention",
        scenario: "Somebody adds FG-00017 as a component of CMP-00057, which FG-00017 already contains twice over.",
        steps: [
            {does: "Save the line.",
             then: "Refused: CMP-00057 already contains FG-00017 somewhere below it; this line would make a bill that contains itself. Nothing is stored."},
            {does: "Try an item as its own component, or a Document as a component.",
             then: "Refused by name as well: an item cannot contain itself, and a bill joins two Items only."},
        ],
        samples: [
            {caption: "The refusals, by code", kind: "text",
             body: "CC-ORBM-CYCLE  the component already contains the assembly below it\nCC-ORBM-SELF   an item containing itself\nCC-ORBM-ITEM   a parent or component that is not an Item"},
        ],
        trap: {
            heading: "Refused before it exists, whichever door writes it",
            body: "The check runs where the line is written, so the editor, the relationship grid and the public API all meet it. A product that validated the bill when it was exploded would store the loop and discover it when a build plan tried to cost it.",
        },
    },
    {
        slug: "units-of-measure",
        scenario: "An adhesive is bought by the kilogram and consumed by the gram, and a tenant stocks a fastener by the box of a hundred.",
        steps: [
            {does: "Open Units of Measure.",
             then: "Every unit by dimension with the base: Mass's base is G and KG carries 1000; Count's base is EA and SET has no factor."},
            {does: "As an administrator, give the tenant's BOX unit a factor of 100 against EA.",
             then: "A bill line in boxes now rolls up in each, and lots held in boxes total with lots held in each."},
            {does: "Try to give KG a different factor.",
             then: "Refused: a platform unit's factor is fixed. A tenant that could re-declare the kilogram would have a bill nobody else could read."},
        ],
        samples: [
            {caption: "The refusals, by code", kind: "text",
             body: "CC-UNIT-PLAT  saving over a platform unit's factor\nCC-UNIT-DIM   kilograms do not become each"},
        ],
        trap: {
            heading: "A unit with no factor is an answer",
            body: "A SET is however many the kit says, so it has no factor and nothing guesses one. A roll-up that meets a SET says the component does not convert; a supplier row whose buy and use units do not convert is refused until the supplier says how many. Silence here would be a wrong number applied silently.",
        },
        api: "GET /t3/v1/units_of_measure",
        guide: {file: "units-of-measure.html", label: "Units of measure"},
    },

    {
        slug: "line-alternates",
        scenario: "The housing sub-assembly's screw line names CMP-00057. A second qualified screw, CMP-00061, may be used instead when the first is short.",
        steps: [
            {does: "On SUB-00037's Bill of materials tab, choose Add substitute on the screw line, pick CMP-00061, quantity 4, unit EA, and Save.",
             then: "The dialog asked no designator and no configuration: both are the primary's. The explosion now lists 'or CMP-00061' under the screw line, indented and marked."},
            {does: "Switch to Rolled up.",
             then: "CMP-00057 is rolled up as before and CMP-00061 is not there at all: a substitute is counted in nothing until somebody chooses it."},
            {does: "Try to remove the screw line.",
             then: "Refused: the line has a substitute; remove the substitute first, then the line."},
        ],
        samples: [
            {caption: "The refusals, by code", kind: "text",
             body: "CC-ORBS-SAME  a substitute that is the primary's own component\nCC-ORBS-NEST  a substitute for another substitute\nCC-ORBS-HAS   removing a line that still has substitutes"},
        ],
        trap: {
            heading: "A substitute is not a second line",
            body: "Two lines for two screws say the assembly needs both; a line and its substitute say it needs one or the other. The explosion shows the substitute's quantity extended so a buyer can read what choosing it would mean, and keeps it out of every total so that nothing reads as double.",
        },
        api: "POST /t3/v1/bom_lines with SubstituteForUSID",
        guide: {file: "bill-of-materials.html", label: "Bill of materials"},
    },

    // ── Stock, orders and receipts ──────────────────────────────────────────────────────────
    {
        slug: "lots",
        scenario: "A lot of 2,000 screws arrived, 150 were issued to a build, and a count found 1,840.",
        steps: [
            {does: "Open the lot's Form tab and read Quantity on hand.",
             then: "1,850: the Receipt of 2,000 less the Issue of 150, computed from the Transfers table and shown read-only."},
            {does: "Add a cycle count row of 1,840.",
             then: "The system quantity 1,850 is stamped beside it and Action needed is set, because the two differ. The quantity on hand is still 1,850."},
            {does: "Add an Adjustment Down of 10 with the reason.",
             then: "Quantity on hand 1,840, and the history shows why: a count found ten fewer, corrected on this date by this person."},
        ],
        trap: {
            heading: "A count never changes the quantity",
            body: "If the count could write the stock, the ledger would be a number somebody typed. The count records what was found; the correction is a transfer with its reason; the stock is the sum. That separation is what makes the number defensible.",
        },
        api: "GET /t3/v1/lots",
        guide: {file: "inventory-and-lots.html", label: "Inventory and lots"},
    },
    {
        slug: "inventory",
        scenario: "Three lots of the same adhesive: 2 kg, 500 g and 1 kg, two of them in a store you may not see.",
        steps: [
            {does: "Open the inventory's Lots tab as a persona that can see all three.",
             then: "On hand across the lots you can see: 3500 G, the lots' units converted to the mass base unit. The lots are listed with status, quantity and location."},
            {does: "Open it as a persona that can see one.",
             then: "500 G, and '2 lots are not visible to you and not counted'. The two are listed as their IDs in grey with no link."},
            {does: "Try to add a lot of a different item.",
             then: "Refused: every lot held must be a lot of the inventory's item."},
        ],
        samples: [
            {caption: "The refusals, by code", kind: "text",
             body: "CC-INVL-MISMATCH  a lot of a different item\nCC-INVL-ITEM      an inventory that names no item yet"},
        ],
        trap: {
            heading: "The total is yours, not the inventory's",
            body: "No total is written on the inventory's form, because one would differ per reader and would hand a reader the stock of lots they cannot open. The number above the list is computed for you, over what you can see, and says what it left out.",
        },
        guide: {file: "inventory-and-lots.html", label: "Inventory and lots"},
    },
    {
        slug: "purchase-orders",
        scenario: "Order 500 screws from the approved supplier, taxable, with shipping.",
        steps: [
            {does: "Create a Purchase Order, choose the supplier and USD, and add a line naming CMP-00057 with quantity 500.",
             then: "The supplier's part number, buy unit and price are filled from the supplier's own row for the item. Save computes the line total, subtotal, tax and total at the cent."},
            {does: "Try to release an order with no lines, or one whose PO status is Void.",
             then: "Refused by name."},
        ],
        samples: [
            {caption: "The refusals, by code", kind: "text",
             body: "CC-PORD-RLSE  releasing an order with no supplier or no lines\nCC-OOAM-OVER  a total above every amount band"},
        ],
        trap: {
            heading: "PO status is not the lifecycle",
            body: "A released order is the controlled document; Open, Closed or Void is where receiving stands. An order is released once and stays released; it closes when every line is received and reopens when a receipt is voided. Two states, two questions.",
        },
        guide: {file: "purchase-orders-and-receipts.html", label: "Purchase orders and receipts"},
    },
    {
        slug: "receipts",
        scenario: "300 of the 500 screws arrive on Tuesday, and the rest on Friday with twenty extra.",
        steps: [
            {does: "Create a Receivable naming the order and enter Received now 300 on the line.",
             then: "Remaining 200. Release it: the order line's received quantity becomes 300 and the order stays Open."},
            {does: "Friday: a second receivable, Received now 220.",
             then: "Received before is 300, from the released Tuesday receipt. 220 is within the order's 5% over-receipt tolerance; 230 would be refused. Release it and the order closes."},
        ],
        samples: [
            {caption: "The refusal, by code", kind: "text",
             body: "CC-RCPT-OVER  a quantity beyond ordered plus tolerance less received before"},
        ],
        trap: {
            heading: "Only a released receipt counts",
            body: "A draft receipt counts toward nothing, so two people receiving the same delivery cannot double it until one of them releases, and a released receipt that was wrong is voided, which recomputes the order without it and reopens it. Received before is read from the other released receipts, never typed.",
        },
        guide: {file: "purchase-orders-and-receipts.html", label: "Purchase orders and receipts"},
    },
    {
        slug: "lot-from-receipt",
        scenario: "The adhesive arrived: 2 kg on one receipt line, bought by the kilogram and used by the gram, inspection required.",
        steps: [
            {does: "On the released receivable's Lots tab, choose Create lot on the line, quantity 2.",
             then: "LOT-00151 is created in grams, 2000 G, with the arithmetic in its first transfer's reason; quarantined, because the receipt asked for inspection, with a Receipt into the location and a Move into Quarantine already recorded."},
            {does: "Try to lot 3 from a 2 kg line.",
             then: "Refused: at most what arrived less what is already in lots from that line."},
        ],
        samples: [
            {caption: "The refusal, by code", kind: "text",
             body: "CC-LOTR-OVER  a lot for more than the line has left unlotted"},
        ],
        trap: {
            heading: "The lot is in use units, and the receipt is in buy units",
            body: "The receipt says 2 KG because that is what was ordered and delivered; the lot says 2000 G because that is what is consumed. The conversion comes from the supplier's row for the item, or from the units' factors when the row gives none, and the transfer's reason shows the arithmetic so nobody has to trust it.",
        },
        guide: {file: "purchase-orders-and-receipts.html", label: "Purchase orders and receipts"},
    },
    {
        slug: "approved-suppliers",
        scenario: "Two suppliers offer the titanium screw; one is the default and the other is kept qualified.",
        steps: [
            {does: "On each supplier's record, in Supplied Items, add the screw with the part number, price, lead time, buy unit and use unit, and tick Primary on one.",
             then: "Saving the second supplier with Primary ticked is refused naming the first, because a primary is who you buy from by default and two defaults are none."},
            {does: "Open CMP-00057's Suppliers tab.",
             then: "Both rows, the primary first, with ASL status, part number, price, lead time, and the buy and use units; a supplier you may not open is its ID in grey."},
        ],
        samples: [
            {caption: "The refusals, by code", kind: "text",
             body: "CC-SUPI-PRIM  a second primary supplier for an item\nCC-SUPI-DIM   buy and use units that do not convert and no conversion given"},
        ],
        trap: {
            heading: "The fact lives on the supplier, and the item reads it",
            body: "Copying supplier rows onto every item type's form would be a second source of one fact, so the approved supplier list is the supplier's Supplied Items table and nothing else. The list is advisory, by decision: it tells a buyer who is approved; it does not stop an order to somebody who is not.",
        },
        api: "GET /t3/v1/approved_suppliers?ItemObjectUSID=…",
        guide: {file: "purchase-orders-and-receipts.html", label: "Purchase orders and receipts"},
    },

    // ── The assistant and the API ───────────────────────────────────────────────────────────
    {
        slug: "assistant-reads-as-you",
        scenario: "Ask: what is FG-00017 made of, and how much is left of LOT-00151?",
        steps: [
            {does: "Ask the assistant, acting as a manufacturing persona.",
             then: "It explodes the bill under your persona and reads the lot's transfers, and answers with the lines and the quantity on hand, naming the records by ID. A component you may not see is reported as its ID, exactly as the screen shows it."},
            {does: "Ask the same question as a persona that cannot see the lot.",
             then: "'No record you can see matches LOT-00151. If you expected one, check the persona you are acting as.' It does not say the lot does not exist, because it may only be out of view."},
        ],
        samples: [
            {caption: "A refusal reaches the model by its code", kind: "json",
             body: "{ \"refused\": { \"Code\": \"CC-PRSN-NONE\",\n               \"Message\": \"No persona is active.\" } }"},
        ],
        trap: {
            heading: "A tool that forgot the persona would not error",
            body: "The service adds the visibility clause only when a persona is set, so a forgetful read would return the tenant's every record and look like a generous answer. Every operation in the package is tested for forwarding the persona and for refusing without one, because the twentieth tool is the one that would forget.",
        },
    },
    {
        slug: "assistant-drafts",
        scenario: "Ask the assistant to raise a change order to retire the old gowning SOP.",
        steps: [
            {does: "Ask.",
             then: "A proposal card under the answer: the title, the records it would cover, and what approving it would do. Nothing has been created."},
            {does: "Approve the card.",
             then: "The change order is created under your persona, with every rule of the write applied at that moment; the proposal records who approved it and when. Reject it and nothing happens."},
        ],
        trap: {
            heading: "The assistant never says it is done",
            body: "It drafted; you approved; the product did it under you. The assistant cannot sign, approve, release, deprecate, void or close anything, and if asked to it says so and offers the draft of the thing it can propose.",
        },
    },
    {
        slug: "documentation-answers",
        scenario: "Ask: what happens when I release a receipt?",
        steps: [
            {does: "Ask the assistant.",
             then: "It searches the guides and answers from the Purchase orders and receipts guide: the received quantities are written back to the order, the order closes when every line is complete. It names the section, and the link under the answer opens the guide there."},
        ],
        samples: [
            {caption: "What the search returns", kind: "json",
             body: "{ \"Path\": \"/docs/purchase-orders-and-receipts.html#receipt-release\",\n  \"Heading\": \"What releasing a receipt does to the order\" }"},
        ],
        trap: {
            heading: "The assistant never writes a URL itself",
            body: "The links under an answer are drawn by the app from the sections the search returned; a model-written link would be a guess at an address. If the guides do not cover a question, the answer says so rather than inventing a behaviour the product does not have.",
        },
    },
    {
        slug: "public-api",
        scenario: "A mail gateway posts demands, and an ERP reads the bills of materials, with one credential each.",
        steps: [
            {does: "Issue an API key authorised on the PreparedQuote entity for write and another on the Item entity for read and write.",
             then: "The first can post quote requests and nothing else; the second can explode a bill, read where-used and units, and write a line in its own words to /t3/v1/bom_lines, where the cycle, quantity, configuration and change-control rules apply exactly as on the screen."},
            {does: "Name a document version in the idoc-response header.",
             then: "The answer is in that shape, or refused by code if the action does not serve it, so a caller is never quietly answered in a newer shape than it asked for."},
        ],
        samples: [
            {caption: "The refusal for a shape the action does not serve", kind: "text",
             body: "CC-QMSD-IDOC  this action does not serve that document; it serves v1.BillOfMaterialsExplosion-001, -002"},
        ],
        trap: {
            heading: "An API key is not a person",
            body: "A key cannot assert which portal user submitted a demand, so the field that names one is not accepted from the API; and a key reads under the persona headers it sends, so what it cannot see is not found. The same refusals, by the same codes, as the screens.",
        },
        api: "GET /t3/v1/bom_explosion, POST /t3/v1/bom_lines, POST /t3/v1/quote_requests",
    },
];

export const EXAMPLE_BY_SLUG: Record<string, CapabilityExample> =
    Object.fromEntries(EXAMPLES.map(one => [one.slug, one]));
